import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  apiDataFetchingAtom,
  apiDataFetchingErrorsAtom,
  apiPost,
  BookingApi,
  bookingsPollingStateAtom,
  castErrorResult,
  companyIdSelector,
  createErrorResult,
  currentBulkSetBookingsTokenAtom,
  defaultBookingsPollingState,
  DrawerType,
  errors,
  getBookingProduceJoinPreviewPath,
  IApiError,
  IProduceJoinDto,
  IProduceJoinResponse,
  IRydeProcess,
  Log,
  normalizeErrors,
  OperatorDescriptor,
  PLURAL_KEY,
  RydeProcessTitle,
  RydeUiUnit,
  SimpleResult,
  simpleResultErrorEmptyResponse,
  simpleResultInvalidExecutionResult,
  useDrawers,
  warnAndToastSimpleResult,
  type IBulkCloneBookingsDto,
} from "@ryde/shared";

import { useHighlightEdited } from "../use-highlight-edited";

const entity: RydeUiUnit = "booking";

interface IProduceJoinPreviewParams {
  payload: IProduceJoinDto;
  withGlobalLoader?: boolean;
}

export const useBookingsBulkActions = () => {
  const { t } = useTranslation();
  const setApiServerErrors = useSetRecoilState(apiDataFetchingErrorsAtom);
  const setBookingsFetching = useSetRecoilState(apiDataFetchingAtom);
  const companyId = useRecoilValue(companyIdSelector);
  const { setHighlightedRows } = useHighlightEdited(entity);
  const { openDrawer } = useDrawers();

  const setBookingPollingStateAtom = useSetRecoilState(
    bookingsPollingStateAtom,
  );
  const setBookingPollingTokenStateAtom = useSetRecoilState(
    currentBulkSetBookingsTokenAtom,
  );
  const produceJoinPreview = useEvent(
    async ({ payload, withGlobalLoader = true }: IProduceJoinPreviewParams) => {
      try {
        setApiServerErrors(undefined);
        if (withGlobalLoader) {
          setBookingsFetching(true);
        }
        const requestUrl = getBookingProduceJoinPreviewPath(companyId || 0);
        const data = await apiPost<IProduceJoinResponse>(requestUrl, {
          body: payload,
        });

        if (
          ((payload?.criteria?.id as OperatorDescriptor)?.$in as number[])
            ?.length
        ) {
          setHighlightedRows(
            (payload?.criteria?.id as OperatorDescriptor)?.$in as number[],
          );
        }

        return data;
      } catch (error) {
        Log.errorAndMessageToast({
          message: t("errors.failedToProduceJoin"),
          error: error as Error,
        });
        setApiServerErrors(normalizeErrors(error as IApiError, t));
        return false;
      } finally {
        if (withGlobalLoader) {
          setBookingsFetching(false);
        }
      }
    },
  );

  const bulkArchive = useEvent(
    async (ids: number[]): Promise<SimpleResult<IRydeProcess | void>> => {
      try {
        if (!ids?.length) {
          const noIdsResult = createErrorResult<void>(
            errors.common.noIdsProvidedToBulkAction,
          );
          warnAndToastSimpleResult(noIdsResult, t);
          return noIdsResult;
        }

        setApiServerErrors(undefined);
        setBookingsFetching(true);
        const bulkArchiveResult = await BookingApi.bulkArchive(ids);

        if (!bulkArchiveResult) {
          return simpleResultErrorEmptyResponse(t);
        }

        if (!bulkArchiveResult?.success) {
          const errorsNormalized = normalizeErrors(bulkArchiveResult?.error, t);
          setApiServerErrors(errorsNormalized);
          warnAndToastSimpleResult(bulkArchiveResult, t);
          return bulkArchiveResult;
        }

        const resultData = bulkArchiveResult?.data as IRydeProcess;
        openDrawer<DrawerType.ProcessDrawer>({
          drawerProps: {
            type: DrawerType.ProcessDrawer,
            processId: resultData.id,
            processTitle: RydeProcessTitle.BulkArchive,
            entity,
          },
        });
        return bulkArchiveResult;
      } catch (error) {
        Log.errorAndToast(
          t(errors.common.bulkFailToArchiveEntities, {
            entity: t(`booking.${PLURAL_KEY}`),
          }),
          error,
        );
        const errorNormalized = normalizeErrors(error as IApiError, t);
        setApiServerErrors(errorNormalized);
        return new SimpleResult({ error: errorNormalized });
      } finally {
        setBookingsFetching(false);
      }
    },
  );

  const cleanupBookingsPollingState = useEvent(() => {
    setBookingPollingStateAtom(defaultBookingsPollingState);
    setBookingPollingTokenStateAtom(null);
  });

  const cleanupPreviousProductionData = useEvent(() => {
    cleanupBookingsPollingState();
  });

  const bulkCancel = useEvent(
    async (ids: number[]): Promise<SimpleResult<IRydeProcess>> => {
      try {
        if (!ids?.length) {
          const noIdsResult = createErrorResult<void>(
            errors.common.noIdsProvidedToBulkAction,
          );
          warnAndToastSimpleResult(noIdsResult, t);
          return castErrorResult(noIdsResult);
        }

        setApiServerErrors(undefined);
        setBookingsFetching(true);
        const bulkCancelResult = await BookingApi.bulkCancel(ids);

        if (!bulkCancelResult) {
          return simpleResultErrorEmptyResponse(t);
        }

        if (!bulkCancelResult?.success) {
          const errorsNormalized = normalizeErrors(bulkCancelResult?.error, t);
          setApiServerErrors(errorsNormalized);
          warnAndToastSimpleResult(bulkCancelResult, t);
          return bulkCancelResult;
        }

        if (!bulkCancelResult?.data) {
          return simpleResultInvalidExecutionResult(t);
        }

        const resultData = bulkCancelResult?.data as IRydeProcess;
        openDrawer<DrawerType.ProcessDrawer>({
          drawerProps: {
            type: DrawerType.ProcessDrawer,
            processId: resultData.id,
            processTitle: RydeProcessTitle.BulkCancelBookings,
            entity,
          },
        });
        return bulkCancelResult;
      } catch (error) {
        Log.errorAndToast(
          t("errors.bulkFailToUpdateEntities", {
            entity: t(`booking.${PLURAL_KEY}`),
          }),
          error,
        );
        setApiServerErrors(normalizeErrors(error as IApiError, t));
        const errorNormalized = normalizeErrors(error as IApiError, t);
        setApiServerErrors(errorNormalized);
        return new SimpleResult({ error: errorNormalized });
      } finally {
        setBookingsFetching(false);
      }
    },
  );

  const bulkClone = useEvent(
    async (
      payload: IBulkCloneBookingsDto,
    ): Promise<SimpleResult<IRydeProcess>> => {
      try {
        if (!payload.ids?.length) {
          const noIdsResult = createErrorResult<void>(
            errors.common.noIdsProvidedToBulkAction,
          );
          warnAndToastSimpleResult(noIdsResult, t);
          return castErrorResult(noIdsResult);
        }

        setApiServerErrors(undefined);
        setBookingsFetching(true);
        const bulkCloneResult = await BookingApi.bulkClone(payload);

        if (!bulkCloneResult) {
          return simpleResultErrorEmptyResponse(t);
        }

        if (!bulkCloneResult.success) {
          setApiServerErrors(normalizeErrors(bulkCloneResult.error, t));
          warnAndToastSimpleResult(bulkCloneResult, t);
          return bulkCloneResult;
        }

        if (!bulkCloneResult.data) {
          return simpleResultInvalidExecutionResult(t);
        }

        openDrawer<DrawerType.ProcessDrawer>({
          drawerProps: {
            type: DrawerType.ProcessDrawer,
            processId: bulkCloneResult.data.id,
            processTitle: RydeProcessTitle.BulkCloneBookings,
            entity,
          },
        });
        return bulkCloneResult;
      } catch (error) {
        Log.errorAndToast(t("errors.failedToCopyBookings"), error);
        const errorNormalized = normalizeErrors(error as IApiError, t);
        setApiServerErrors(errorNormalized);
        return new SimpleResult({ error: errorNormalized });
      } finally {
        setBookingsFetching(false);
      }
    },
  );

  return {
    produceJoinPreview,
    bulkArchive,
    bulkCancel,
    bulkClone,
    cleanupPreviousProductionData,
  };
};
